// ANTD Components
import { Form, Input, Button as ANTButton } from "antd";

// Components
import Button, { BackgroundType } from "components/atoms/Button/Button";
import StepInfo from "components/atoms/StepInfo/StepInfo";
import ErrorMessage from "components/molecules/ErrorMessage/ErrorMessage";
import { Head } from "components/molecules/Head/Head";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import StepContainer from "components/molecules/StepContainer/StepContainer";
import MultipleFileUpload from "components/atoms/FileUpload/MultipleFileUpload";
import InputPrefix from "components/atoms/InputPrefix/InputPrefix";

// Styles
import styles from "components/organisms/EditRejectPageTemplate/EditRejectPageTemplate.module.css";
import theme from "styles/theme";

// Constants & Utils
import { EDIT_REJECT_PAGE_TEMPLATE as Constants } from "constants/global";

// Type definitions
import { UploadChangeParam, UploadFile } from "antd/es/upload/interface";
import type { ReimbursementFieldErrorsType } from "containers/MonthlyDeclaration/EditReimbursement/useMonthlyReimbursementEditPage";
import getIcon from "utils/getIcon";
import { FormInstance } from "antd/es/form/Form";

export type OperationTypes = "edit" | "reject";

type EditPageCommonProps = {
	initialValues?: { tenant_remarks?: string; tenant_amount?: number; tenant_documents?: string[] };
	loadingstate?: boolean;
	handleEditSubmission?: () => void;
	originalAmountHandler?: () => void;
	stepsInfo?: Array<{ step: number; subHeader: string; message?: string }>;
	inputFields?: { reason?: string; amount?: number };
	handleSubmit?: (operationType: OperationTypes) => void;
	handleTextAreaChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
	handleInputChange?: React.ChangeEventHandler<HTMLInputElement>;
	handleFileUpload?: (info: UploadChangeParam<UploadFile>) => void;
	fieldErrors?: ReimbursementFieldErrorsType;
	fieldsEdited?: boolean;
	isRejectedByTenant?: boolean;
};

export type EditRejectPageTemplateProps = {
	headTitle: string;
	pageTitle: string;
	pageSubTitle: string;
	operationType: OperationTypes;
	isNextEnabled: boolean;
	uploadFileTypes: string;
	pageGoBack?: () => void;
	handleChange?: () => void;
	form?: FormInstance<any>;
	disabled?: boolean;
	editable?: boolean;
	submissionLoading?: boolean;
} & EditPageCommonProps;

export type RejectPageTemplateProps = {
	operationType: OperationTypes;
	isNextEnabled: boolean;
	uploadFileTypes: string;
	stepsInfo?: Array<{ step: number; subHeader: string; message?: string }>;
	inputFields?: { reason?: string; amount?: number };
	handleSubmit?: (operationType: OperationTypes) => void;
	handleFileUpload?: (info: UploadChangeParam<UploadFile>) => void;
	handleTextAreaChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
	handleInputChange?: React.ChangeEventHandler<HTMLInputElement>;
	fieldErrors?: ReimbursementFieldErrorsType;
};

export type EditPageTemplateProps = {
	operationType: OperationTypes;
	isNextEnabled: boolean;
	uploadFileTypes: string;
	pageGoBack?: () => void;
	handleChange?: () => void;
	disabled?: boolean;
	editable?: boolean;
	form?: FormInstance<any>;
} & EditPageCommonProps;

type ErrorMessageWrapperTypes = {
	fieldErrors?: boolean;
	inputFields?: string | number;
	fieldMessage: string;
	inputMessage: string;
};

const ErrorMessageWrapper = ({ fieldErrors, inputFields, fieldMessage, inputMessage }: ErrorMessageWrapperTypes) => {
	return (
		<>
			{!inputFields && <ErrorMessage classes={styles?.err_message} message={inputMessage} />}
			{fieldErrors && <ErrorMessage classes={styles?.err_message} message={fieldMessage} />}
		</>
	);
};

const getStatus = (value: boolean | undefined) => {
	return value ? "active" : "disabled";
};

const RejectTemplate = ({
	operationType,
	isNextEnabled,
	uploadFileTypes,
	stepsInfo,
	inputFields,
	handleFileUpload,
	handleSubmit,
	handleTextAreaChange,
	handleInputChange,
	fieldErrors
}: RejectPageTemplateProps) => {
	return (
		<Form>
			{stepsInfo?.[0] && (
				<>
					<div className={styles?.step_wrapper}>
						<div className={styles?.step_container_wrapper}>
							<StepContainer {...stepsInfo?.[0]} noMaxWidth />
							<div className="mt-5">
								<StepInfo message={stepsInfo?.[0].message ?? ""} />
							</div>
						</div>
						<div className="h-28 md:w-7/12 md:h-48">{handleFileUpload && <MultipleFileUpload handleFileInput={handleFileUpload} accept={uploadFileTypes} />}</div>
					</div>
					<div className="h-24 md:h-20 md:mb-20"></div>
				</>
			)}
			{stepsInfo?.[1] && (
				<div className={styles?.step_wrapper}>
					<div className={styles?.step_container_wrapper}>
						<StepContainer {...stepsInfo?.[1]} noMaxWidth />
					</div>
					<div className="md:w-7/12">
						<div className={styles?.input_wrapper}>
							<Form.Item
								name={Constants.TEXTAREA_FORM_NAME}
								extra={
									<ErrorMessageWrapper
										fieldErrors={fieldErrors?.reason}
										inputFields={inputFields?.reason}
										fieldMessage={Constants.TEXTAREA_ERROR}
										inputMessage={Constants.ERROR_TEXT}
									/>
								}
							>
								<Input.TextArea
									value={inputFields?.reason}
									data-testid={Constants.TEXTAREA_FORM_NAME}
									placeholder={Constants.TEXTAREA_PLACEHOLDER}
									onChange={handleTextAreaChange}
									className={`${fieldErrors?.reason || !inputFields?.reason ? styles?.basic_textarea_error : styles?.basic_textarea_active}`}
									rows={4}
								/>
							</Form.Item>
						</div>
					</div>
				</div>
			)}
			{stepsInfo?.[2] && (
				<div className={styles?.step_wrapper}>
					<div className={styles?.step_container_wrapper}>
						<StepContainer {...stepsInfo?.[2]} noMaxWidth />
						<div className="mt-5">
							<StepInfo message={stepsInfo?.[2].message ?? ""} />
						</div>
					</div>
					<div className={styles?.step_input_wrapper}>
						<Form.Item
							name={Constants.INPUT_FORM_NAME}
							extra={
								<ErrorMessageWrapper
									fieldErrors={fieldErrors?.amount}
									inputFields={inputFields?.amount}
									fieldMessage={Constants.INPUT_ERROR}
									inputMessage={Constants.INPUT_ERROR}
								/>
							}
						>
							<Input
								value={inputFields?.amount}
								pattern="[0-9]"
								min={0}
								data-testid={Constants.INPUT_FORM_NAME}
								className={`${styles?.basic_input_small} ${fieldErrors?.amount || !inputFields?.amount ? styles?.err : styles?.filled}`}
								placeholder={Constants.INPUT_PLACEHOLDER}
								onChange={handleInputChange}
								prefix={<InputPrefix prefixText={Constants.AMOUNT_INPUT_PREFIX} />}
							/>
						</Form.Item>
						<div className="my-[0.09375rem] md:my-0">
							<Button
								label={Constants.SUBMIT_BUTTON_LABEL}
								background={Constants.SUBMIT_BUTTON_BG as BackgroundType}
								type={!isNextEnabled ? "disabled" : "active"}
								onClick={() => handleSubmit?.(operationType)}
							/>
						</div>
					</div>
				</div>
			)}
		</Form>
	);
};

const EditPageTemplate = ({
	operationType,
	isNextEnabled,
	uploadFileTypes,
	pageGoBack,
	stepsInfo,
	inputFields,
	disabled,
	editable,
	handleFileUpload,
	handleSubmit,
	handleTextAreaChange,
	handleInputChange,
	handleChange,
	fieldErrors,
	form,
	fieldsEdited,
	initialValues,
	handleEditSubmission,
	originalAmountHandler,
	isRejectedByTenant
}: EditPageTemplateProps) => {
	return (
		<Form form={form}>
			{stepsInfo?.[0] && (
				<>
					<div className={styles?.step_wrapper}>
						<div className={styles?.step_container_wrapper}>
							<StepContainer {...stepsInfo?.[0]} noMaxWidth active={!disabled} />
							<div className="mt-5">
								<StepInfo message={stepsInfo?.[0].message ?? ""} />
							</div>
						</div>
						<div className="h-28 md:w-7/12 md:h-48">
							{handleFileUpload && <MultipleFileUpload handleFileInput={handleFileUpload} accept={uploadFileTypes} disabled={disabled} files={initialValues?.tenant_documents} />}
						</div>
					</div>
					<div className="h-24 md:h-20 md:mb-20"></div>
				</>
			)}
			{stepsInfo?.[1] && (
				<div className={styles?.step_wrapper}>
					<div className={styles?.step_container_wrapper}>
						<StepContainer {...stepsInfo?.[1]} noMaxWidth active={!disabled} />
					</div>
					<div className="md:w-7/12">
						<div className={styles?.input_wrapper}>
							<Form.Item
								name={Constants.TEXTAREA_FORM_NAME}
								initialValue={initialValues?.tenant_remarks}
								extra={
									<ErrorMessageWrapper
										fieldErrors={fieldErrors?.reason}
										inputFields={inputFields?.reason}
										fieldMessage={Constants.TEXTAREA_ERROR}
										inputMessage={Constants.ERROR_TEXT}
									/>
								}
							>
								<Input.TextArea
									value={inputFields?.reason}
									data-testid={Constants.TEXTAREA_FORM_NAME}
									placeholder={Constants.TEXTAREA_PLACEHOLDER}
									onChange={handleTextAreaChange}
									className={`${fieldErrors?.reason || !inputFields?.reason ? styles?.basic_textarea_error : styles?.basic_textarea_active}`}
									rows={4}
									disabled={disabled}
								/>
							</Form.Item>
						</div>
					</div>
				</div>
			)}
			{stepsInfo?.[2] && (
				<div className={styles?.step_wrapper}>
					<div className={styles?.step_container_wrapper}>
						<StepContainer {...stepsInfo?.[2]} noMaxWidth active={!disabled} />
						<div className="mt-5">
							<StepInfo message={stepsInfo?.[2].message ?? ""} />
						</div>
					</div>
					<div className={styles?.step_input_wrapper}>
						<Form.Item
							name={Constants.INPUT_FORM_NAME}
							initialValue={initialValues?.tenant_amount}
							extra={
								<ErrorMessageWrapper
									fieldErrors={fieldErrors?.amount}
									inputFields={inputFields?.amount}
									fieldMessage={Constants.INPUT_ERROR}
									inputMessage={Constants.INPUT_ERROR}
								/>
							}
						>
							<Input
								value={inputFields?.amount}
								pattern="[0-9]"
								min={0}
								data-testid={Constants.INPUT_FORM_NAME}
								className={`${styles?.basic_input_small} ${fieldErrors?.amount || !inputFields?.amount ? styles?.err : styles?.filled}`}
								placeholder={Constants.INPUT_PLACEHOLDER}
								onChange={handleInputChange}
								disabled={disabled}
								prefix={<InputPrefix prefixText={Constants.AMOUNT_INPUT_PREFIX} />}
							/>
						</Form.Item>
						{!isRejectedByTenant && (
							<div className="my-[0.09375rem] md:my-0">
								{!disabled && !editable && (
									<Button
										label={Constants.SUBMIT_BUTTON_LABEL}
										background={Constants.SUBMIT_BUTTON_BG as BackgroundType}
										type={getStatus(isNextEnabled)}
										onClick={() => handleSubmit?.(operationType)}
									/>
								)}
								{disabled && (
									<div>
										<div className="flex justify-end">
											<Button label={Constants.EDIT_SUBMISSION_LABEL} onClick={handleEditSubmission} />
										</div>
										<div className="md:flex mt-2.5 md:justify-end">
											<ANTButton className={styles?.inWindow_link} type="link" onClick={originalAmountHandler}>
												<div className={styles?.original_amount_text}>{Constants.ACCEPT_ORIGINAL_AMOUNT_TEXT}</div>
												<div className={styles?.original_amount_icon}>{getIcon({ icon: "inWindow", iconColor: theme.colors.red })}</div>
											</ANTButton>
										</div>
									</div>
								)}
								{!disabled && editable && (
									<div>
										<div className="flex justify-end">
											<Button type={getStatus(fieldsEdited)} label={Constants.SAVE_CHANGES_LABEL} onClick={handleChange} />
										</div>
										<div className="flex mt-2 justify-end">
											<ANTButton className={styles?.inWindow_link} type="link" onClick={pageGoBack}>
												<div className={styles?.goBack_label}>{Constants.GO_BACK}</div>
												<div className={styles?.goBack_icon}>{getIcon({ icon: "inWindow", iconColor: theme.colors.violet })}</div>
											</ANTButton>
										</div>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			)}
		</Form>
	);
};

const EditRejectPageTemplate = ({
	headTitle,
	pageTitle,
	pageSubTitle,
	operationType,
	isNextEnabled,
	uploadFileTypes,
	pageGoBack,
	stepsInfo,
	inputFields,
	disabled,
	editable,
	form,
	handleFileUpload,
	handleSubmit,
	handleTextAreaChange,
	handleInputChange,
	handleChange,
	fieldErrors,
	fieldsEdited,
	initialValues,
	handleEditSubmission,
	originalAmountHandler,
	isRejectedByTenant
}: EditRejectPageTemplateProps): JSX.Element => {
	return (
		<div className={styles?.root}>
			<Head>{headTitle}</Head>
			<PageTitle showBackIcon={true} title={pageTitle} subTitle={pageSubTitle} goBack={pageGoBack} />
			<div className={styles?.main_wrapper}>
				{operationType === Constants.FROM_EDIT ? (
					<EditPageTemplate
						operationType={operationType}
						isNextEnabled={isNextEnabled}
						uploadFileTypes={uploadFileTypes}
						pageGoBack={pageGoBack}
						stepsInfo={stepsInfo}
						inputFields={inputFields}
						disabled={disabled}
						editable={editable}
						handleFileUpload={handleFileUpload}
						handleSubmit={handleSubmit}
						handleTextAreaChange={handleTextAreaChange}
						handleInputChange={handleInputChange}
						handleChange={handleChange}
						fieldErrors={fieldErrors}
						fieldsEdited={fieldsEdited}
						initialValues={initialValues}
						form={form}
						handleEditSubmission={handleEditSubmission}
						originalAmountHandler={originalAmountHandler}
						isRejectedByTenant={isRejectedByTenant}
					/>
				) : (
					<RejectTemplate
						operationType={operationType}
						isNextEnabled={isNextEnabled}
						uploadFileTypes={uploadFileTypes}
						stepsInfo={stepsInfo}
						inputFields={inputFields}
						handleFileUpload={handleFileUpload}
						handleSubmit={handleSubmit}
						handleTextAreaChange={handleTextAreaChange}
						handleInputChange={handleInputChange}
						fieldErrors={fieldErrors}
					/>
				)}
			</div>
		</div>
	);
};
export default EditRejectPageTemplate;
